import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  makeStyles,
  Typography
} from '@material-ui/core';
import AccountCircleOutlinedIcon from '@material-ui/icons/AccountCircleOutlined';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import CloseIcon from '@material-ui/icons/Close';
import FileCopyOutlinedIcon from '@material-ui/icons/FileCopyOutlined';
import GetAppOutlinedIcon from '@material-ui/icons/GetAppOutlined';
import HeadsetMicOutlinedIcon from '@material-ui/icons/HeadsetMicOutlined';
import LanguageOutlinedIcon from '@material-ui/icons/LanguageOutlined';
import LockOutlinedIcon from '@material-ui/icons/LockOutlined';

const WEBSITE = 'vgps.vn';
const APP_DOWNLOAD_LINK = 'https://vgps.vn/app-download';
const SUPPORT_PHONE = '0706.90.3333';

function AgencyAccountInfoDialog({ open, onClose, accountInfo, type = 'agency' }) {
  const classes = useStyles();
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (open) {
      setCopied(false);
    }
  }, [open]);

  const rows = [
    {
      label: 'Tài khoản',
      value: accountInfo?.username || '',
      icon: <AccountCircleOutlinedIcon />,
      highlight: true
    },
    {
      label: 'Mật khẩu',
      value: accountInfo?.password || '',
      icon: <LockOutlinedIcon />,
      highlight: true
    },
    {
      label: 'Website',
      value: WEBSITE,
      icon: <LanguageOutlinedIcon />
    },
    {
      label: 'Link tải App',
      helper: 'Dành cho cả IOS và Android',
      value: APP_DOWNLOAD_LINK,
      icon: <GetAppOutlinedIcon />,
      href: APP_DOWNLOAD_LINK
    },
    {
      label: 'Hỗ trợ CSKH VGPS',
      value: SUPPORT_PHONE,
      icon: <HeadsetMicOutlinedIcon />
    }
  ];

  const copyText = [
    `Tài khoản: ${accountInfo?.username || ''}`,
    `Mật khẩu: ${accountInfo?.password || ''}`,
    `Website: ${WEBSITE}`,
    'Link tải App (Dành cho cả IOS và Android)',
    APP_DOWNLOAD_LINK,
    `Hỗ trợ CSKH VGPS: ${SUPPORT_PHONE}`
  ].join('\n');

  const fallbackCopy = text => {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.top = '-9999px';
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand('copy');
    document.body.removeChild(textarea);
  };

  const handleCopyAll = () => {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(copyText).then(() => {
        setCopied(true);
      }).catch(() => {
        fallbackCopy(copyText);
        setCopied(true);
      });
    } else {
      fallbackCopy(copyText);
      setCopied(true);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ className: classes.paper }}
    >
      <DialogTitle disableTypography className={classes.header}>
        <Box className={classes.successMark}>
          <CheckCircleOutlineIcon />
        </Box>
        <Box className={classes.headerText}>
          <Typography className={classes.title}>Thông tin tài khoản</Typography>
          <Typography className={classes.subtitle}>
            {copied
              ? 'Đã copy thông tin tài khoản.'
              : `Gửi thông tin này cho ${type === 'customer' ? 'khách hàng' : 'đại lý'} để bắt đầu sử dụng hệ thống.`}
          </Typography>
        </Box>
        <IconButton
          className={classes.copyButton}
          onClick={handleCopyAll}
          title="Copy tất cả thông tin"
        >
          <FileCopyOutlinedIcon fontSize="small" />
        </IconButton>
        <IconButton className={classes.closeButton} onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent className={classes.dialogContent}>
        <Box className={classes.content}>
          {rows.map((row, index) => (
            <React.Fragment key={row.label}>
              <Box className={classes.row}>
                <Box className={classes.iconWrap}>{row.icon}</Box>
                <Box className={classes.rowBody}>
                  <Box className={classes.rowTitle}>
                    <Typography className={classes.label}>{row.label}</Typography>
                    {row.helper && (
                      <Typography className={classes.helper}>
                        {row.helper}
                      </Typography>
                    )}
                  </Box>
                  {row.href ? (
                    <a
                      className={classes.linkValue}
                      href={row.href}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {row.value}
                    </a>
                  ) : (
                    <Typography
                      className={
                        row.highlight ? classes.highlightValue : classes.value
                      }
                    >
                      {row.value}
                    </Typography>
                  )}
                </Box>
              </Box>
              {index < rows.length - 1 && <Divider />}
            </React.Fragment>
          ))}
        </Box>
      </DialogContent>
      <DialogActions className={classes.actions}>
        <Button
          className={classes.doneButton}
          color="primary"
          variant="contained"
          onClick={onClose}
        >
          Đóng
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const useStyles = makeStyles(theme => ({
  paper: {
    borderRadius: '18px',
    overflow: 'hidden',
    boxShadow: '0 24px 80px rgba(12, 17, 50, 0.22)'
  },
  header: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    padding: theme.spacing(3, 7, 2.5, 3),
    background:
      'linear-gradient(135deg, #F7FAFF 0%, #FFFFFF 48%, #FFF4F6 100%)',
    borderBottom: '1px solid rgba(12, 17, 50, 0.08)'
  },
  successMark: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '48px',
    height: '48px',
    flex: '0 0 48px',
    borderRadius: '14px',
    color: '#0F9F6E',
    background: '#E8F8F1',
    boxShadow: 'inset 0 0 0 1px rgba(15, 159, 110, 0.14)',
    '& svg': {
      fontSize: '28px'
    }
  },
  headerText: {
    minWidth: 0
  },
  title: {
    color: '#0C1132',
    fontSize: '22px',
    lineHeight: 1.25,
    fontWeight: 700
  },
  subtitle: {
    color: '#667085',
    fontSize: '14px',
    lineHeight: 1.5,
    marginTop: theme.spacing(0.5)
  },
  closeButton: {
    position: 'absolute',
    top: theme.spacing(1.5),
    right: theme.spacing(1.5),
    color: '#667085',
    background: 'rgba(255, 255, 255, 0.78)',
    '&:hover': {
      background: '#FFFFFF'
    }
  },
  copyButton: {
    position: 'absolute',
    top: theme.spacing(1.5),
    right: theme.spacing(8),
    color: '#D9042F',
    background: 'rgba(217, 4, 47, 0.08)',
    '&:hover': {
      background: 'rgba(217, 4, 47, 0.14)'
    }
  },
  dialogContent: {
    padding: theme.spacing(2.5, 3, 1)
  },
  content: {
    border: '1px solid rgba(12, 17, 50, 0.08)',
    borderRadius: '14px',
    background: '#FFFFFF',
    overflow: 'hidden'
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.75),
    padding: theme.spacing(1.75, 2),
    [theme.breakpoints.down('xs')]: {
      alignItems: 'flex-start',
      padding: theme.spacing(1.5)
    }
  },
  iconWrap: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '38px',
    height: '38px',
    flex: '0 0 38px',
    borderRadius: '12px',
    color: '#D9042F',
    background: 'rgba(217, 4, 47, 0.08)',
    '& svg': {
      fontSize: '21px'
    }
  },
  rowBody: {
    display: 'grid',
    gridTemplateColumns: '190px minmax(0, 1fr)',
    alignItems: 'center',
    gap: theme.spacing(2),
    width: '100%',
    minWidth: 0,
    [theme.breakpoints.down('xs')]: {
      gridTemplateColumns: '1fr',
      gap: theme.spacing(0.75)
    }
  },
  rowTitle: {
    minWidth: 0
  },
  label: {
    color: '#344054',
    fontSize: '14px',
    lineHeight: 1.35,
    fontWeight: 700
  },
  helper: {
    color: '#98A2B3',
    fontSize: '12px',
    lineHeight: 1.35,
    marginTop: '2px'
  },
  value: {
    color: '#101828',
    fontSize: '15px',
    fontWeight: 600,
    wordBreak: 'break-word'
  },
  highlightValue: {
    display: 'inline-flex',
    width: 'fit-content',
    maxWidth: '100%',
    color: '#0C1132',
    fontSize: '15px',
    fontWeight: 800,
    lineHeight: 1.35,
    wordBreak: 'break-word',
    borderRadius: '999px',
    padding: theme.spacing(0.75, 1.5),
    background: '#F2F4F7',
    boxShadow: 'inset 0 0 0 1px rgba(12, 17, 50, 0.08)'
  },
  linkValue: {
    color: '#D9042F',
    fontSize: '15px',
    fontWeight: 700,
    lineHeight: 1.35,
    textDecoration: 'none',
    wordBreak: 'break-word',
    '&:hover': {
      textDecoration: 'underline'
    }
  },
  actions: {
    justifyContent: 'center',
    padding: theme.spacing(1.5, 3, 3)
  },
  doneButton: {
    minWidth: '140px',
    height: '42px',
    borderRadius: '999px',
    textTransform: 'none',
    fontWeight: 700,
    background: '#D9042F',
    boxShadow: '0 10px 24px rgba(217, 4, 47, 0.24)',
    '&:hover': {
      background: '#B70328',
      boxShadow: '0 12px 28px rgba(217, 4, 47, 0.30)'
    }
  }
}));

export default React.memo(AgencyAccountInfoDialog);
